import type React from "react"

import { useState, useEffect } from "react"
import { Users, UserPlus, Search, X, Check } from "lucide-react"
import { HRService } from "@/features/hr"
import { OrganizationService } from "@/features/organizations"
import { AgentAssignmentShimmer, OrganizationHierarchyShimmer, TeamShimmer } from "@/shared/components"

interface Agent {
  id: string
  userId?: string
  firstName: string
  lastName: string
  email: string
  status: "ACTIVE" | "PENDING" | "DEACTIVATED"
  organizationId?: string
  teamId?: string
  organizationName?: string
  teamName?: string
}

interface Team {
  id: string
  name: string
  organizationId: string
  organizationName: string
  organizationType?: string
  organizationLevel?: string
  memberCount: number
  maxMembers: number
  managerName: string
}

interface Organization {
  id: string
  name: string
  type: "E" | "D" | "C" | "B" | "A"
  parentId?: string
  managerName?: string
}

interface LicensedAgentAssignmentManagerProps {
  onError: (error: string) => void
}

export default function LicensedAgentAssignmentManager({ onError }: LicensedAgentAssignmentManagerProps) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([])
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set())
  const [draggedAgent, setDraggedAgent] = useState<Agent | null>(null)
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [assigningAgent, setAssigningAgent] = useState<string | null>(null)
  const [dragOverTeam, setDragOverTeam] = useState<string | null>(null)
  const [recentlyUpdatedTeams, setRecentlyUpdatedTeams] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)
  const [loadingTeams, setLoadingTeams] = useState(false)

  const [currentSelection, setCurrentSelection] = useState({
    eLevel: "",
    dLevel: "",
    cLevel: "",
    bLevel: "",
  })

  const [visibleTeams, setVisibleTeams] = useState<Team[]>([])

  const [batchAssignment, setBatchAssignment] = useState({
    eLevel: "",
    dLevel: "",
    cLevel: "",
    bLevel: "",
    teamId: "",
  })

  const [filters, setFilters] = useState({
    search: "",
    status: "",
  })

  const [allOrganizationsByLevel, setAllOrganizationsByLevel] = useState({
    E: [] as Organization[],
    D: [] as Organization[],
    C: [] as Organization[],
    B: [] as Organization[],
    A: [] as Organization[],
  })

  const [cascadingOptions, setCascadingOptions] = useState({
    eLevel: [] as Organization[],
    dLevel: [] as Organization[],
    cLevel: [] as Organization[],
    bLevel: [] as Organization[],
  })

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load ALL organizations at once
      const [emOrgs, dmOrgs, cmOrgs, bmOrgs, amOrgs] = await Promise.all([
        OrganizationService.getOrganizationsByLevel('EM'),
        OrganizationService.getOrganizationsByLevel('DM'),
        OrganizationService.getOrganizationsByLevel('CM'),
        OrganizationService.getOrganizationsByLevel('BM'),
        OrganizationService.getOrganizationsByLevel('AM')
      ])
      
      // Map database organization types to frontend types (E, D, C, B, A)
      const mappedEOrgs = emOrgs.map(org => ({ ...org, type: 'E' as const }))
      const mappedDOrgs = dmOrgs.map(org => ({ ...org, type: 'D' as const }))
      const mappedCOrgs = cmOrgs.map(org => ({ ...org, type: 'C' as const }))
      const mappedBOrgs = bmOrgs.map(org => ({ ...org, type: 'B' as const }))
      const mappedAOrgs = amOrgs.map(org => ({ ...org, type: 'A' as const }))
      
      const allOrgs = [
        ...mappedEOrgs,
        ...mappedDOrgs,
        ...mappedCOrgs,
        ...mappedBOrgs,
        ...mappedAOrgs
      ]
      
      setOrganizations(allOrgs)

      // Store all organizations by level for filtering
      setAllOrganizationsByLevel({
        E: mappedEOrgs,
        D: mappedDOrgs,
        C: mappedCOrgs,
        B: mappedBOrgs,
        A: mappedAOrgs,
      })

      // Load teams - initially empty since no organization is selected
      setTeams([])

      // Load agents
      const agentsData = await HRService.getAgentsForAssignment()
      
      // Ensure agentsData.agents is an array
      let agentsArray = Array.isArray(agentsData?.agents) ? agentsData.agents : []
      
      // If no agents from API, use mock data as fallback
      if (agentsArray.length === 0) {
        agentsArray = []
      }
      setAgents(agentsArray)

      // Set initial cascading options - E level is always available
      setCascadingOptions({
        eLevel: mappedEOrgs,
        dLevel: [],
        cLevel: [],
        bLevel: [],
      })
    } catch (error) {
      console.error("Error loading data:", error)
      onError("Failed to load data. Please try again.")
      
      // Set empty arrays as fallback
      setOrganizations([])
      setTeams([])
      setAgents([])
      
      // Set empty cascading options
      setAllOrganizationsByLevel({
        E: [],
        D: [],
        C: [],
        B: [],
        A: [],
      })
      setCascadingOptions({
        eLevel: [],
        dLevel: [],
        cLevel: [],
        bLevel: [],
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [onError])


  // Helper function to count agents per organization
  const getAgentCountForOrganization = (organizationId: string): number => {
    return agents.filter(agent => agent.organizationId === organizationId).length
  }

  // Helper function to determine the deepest level with organizations after current selection
  const getDeepestLevel = (currentLevel: string, selectedId: string): { level: string, orgs: Organization[] } | null => {
    const selectedOrg = organizations.find(org => org.id === selectedId)
    if (!selectedOrg) return null

    // Check each level in order to find the deepest one with organizations
    if (currentLevel === 'eLevel') {
      // Check if there are D level orgs under this E level
      const dOrgs = allOrganizationsByLevel.D.filter(org => org.parentId === selectedId)
      if (dOrgs.length > 0) return null // Has D level, so not deepest yet
      
      // No D level, E is the deepest
      return { level: 'E', orgs: allOrganizationsByLevel.E.filter(org => org.id === selectedId) }
    }
    
    if (currentLevel === 'dLevel') {
      const cOrgs = allOrganizationsByLevel.C.filter(org => org.parentId === selectedId)
      if (cOrgs.length > 0) return null
      
      return { level: 'D', orgs: allOrganizationsByLevel.D.filter(org => org.id === selectedId) }
    }
    
    if (currentLevel === 'cLevel') {
      const bOrgs = allOrganizationsByLevel.B.filter(org => org.parentId === selectedId)
      if (bOrgs.length > 0) return null
      
      return { level: 'C', orgs: allOrganizationsByLevel.C.filter(org => org.id === selectedId) }
    }
    
    if (currentLevel === 'bLevel') {
      const aOrgs = allOrganizationsByLevel.A.filter(org => org.parentId === selectedId)
      if (aOrgs.length > 0) return null
      
      return { level: 'B', orgs: allOrganizationsByLevel.B.filter(org => org.id === selectedId) }
    }
    
    return null
  }

  const updateCascadingOptions = (level: string, selectedId: string) => {
    if (level === "eLevel") {
      // Filter D level organizations from already loaded data
      const dLevelOptions = allOrganizationsByLevel.D.filter(org => org.parentId === selectedId)
      
      setCascadingOptions((prev) => ({
        ...prev,
        dLevel: dLevelOptions,
        cLevel: [],
        bLevel: [],
      }))
      setCurrentSelection({
        eLevel: selectedId,
        dLevel: "",
        cLevel: "",
        bLevel: "",
      })
      
      // Check if D level exists
      if (dLevelOptions.length === 0) {
        // No D level orgs, E is the deepest - show it as a team
        const selectedEOrg = allOrganizationsByLevel.E.find(org => org.id === selectedId)
        if (selectedEOrg) {
          const teamsFromOrgs = [{
            id: selectedEOrg.id,
            name: selectedEOrg.name,
            organizationId: selectedEOrg.id,
            organizationName: selectedEOrg.name,
            organizationType: 'E',
            organizationLevel: 'E Level',
            memberCount: getAgentCountForOrganization(selectedEOrg.id),
            maxMembers: 100,
            managerName: (selectedEOrg as any).managerName || 'Manager'
          }]
          setVisibleTeams(teamsFromOrgs)
        }
      } else {
        // Check if any D level orgs have C level children
        const hasCLevelChildren = dLevelOptions.some(dOrg => 
          allOrganizationsByLevel.C.some(cOrg => cOrg.parentId === dOrg.id)
        )
        
        if (!hasCLevelChildren) {
          // D level exists but none have C children - show D as teams directly
          const teamsFromOrgs = dLevelOptions.map(org => ({
            id: org.id,
            name: org.name,
            organizationId: org.id,
            organizationName: org.name,
            organizationType: 'D',
            organizationLevel: 'D Level',
            memberCount: getAgentCountForOrganization(org.id),
            maxMembers: 100,
            managerName: (org as any).managerName || 'Manager'
          }))
          setVisibleTeams(teamsFromOrgs)
          // Clear D level dropdown since we're showing them as teams
          setCascadingOptions((prev) => ({
            ...prev,
            dLevel: [],
          }))
        } else {
          // Has C level children, don't show teams yet - wait for D selection
          setVisibleTeams([])
        }
      }
    } else if (level === "dLevel") {
      // Filter C level organizations from already loaded data
      const cLevelOptions = allOrganizationsByLevel.C.filter(org => org.parentId === selectedId)
      
      setCascadingOptions((prev) => ({
        ...prev,
        cLevel: cLevelOptions,
        bLevel: [],
      }))
      setCurrentSelection((prev) => ({
        ...prev,
        dLevel: selectedId,
        cLevel: "",
        bLevel: "",
      }))
      
      // Check if C level exists
      if (cLevelOptions.length === 0) {
        // No C level orgs, D is the deepest - show it as a team
        const selectedDOrg = allOrganizationsByLevel.D.find(org => org.id === selectedId)
        if (selectedDOrg) {
          const teamsFromOrgs = [{
            id: selectedDOrg.id,
            name: selectedDOrg.name,
            organizationId: selectedDOrg.id,
            organizationName: selectedDOrg.name,
            organizationType: 'D',
            organizationLevel: 'D Level',
            memberCount: getAgentCountForOrganization(selectedDOrg.id),
            maxMembers: 100,
            managerName: (selectedDOrg as any).managerName || 'Manager'
          }]
          setVisibleTeams(teamsFromOrgs)
        }
      } else {
        // Check if any C level orgs have B level children
        const hasBLevelChildren = cLevelOptions.some(cOrg => 
          allOrganizationsByLevel.B.some(bOrg => bOrg.parentId === cOrg.id)
        )
        
        if (!hasBLevelChildren) {
          // C level exists but none have B children - show C as teams directly
          const teamsFromOrgs = cLevelOptions.map(org => ({
            id: org.id,
            name: org.name,
            organizationId: org.id,
            organizationName: org.name,
            organizationType: 'C',
            organizationLevel: 'C Level',
            memberCount: getAgentCountForOrganization(org.id),
            maxMembers: 100,
            managerName: (org as any).managerName || 'Manager'
          }))
          setVisibleTeams(teamsFromOrgs)
          // Clear C level dropdown since we're showing them as teams
          setCascadingOptions((prev) => ({
            ...prev,
            cLevel: [],
          }))
        } else {
          // Has B level children, don't show teams yet - wait for C selection
          setVisibleTeams([])
        }
      }
    } else if (level === "cLevel") {
      // Filter B level organizations from already loaded data
      const bLevelOptions = allOrganizationsByLevel.B.filter(org => org.parentId === selectedId)
      
      setCascadingOptions((prev) => ({
        ...prev,
        bLevel: bLevelOptions,
      }))
      setCurrentSelection((prev) => ({
        ...prev,
        cLevel: selectedId,
        bLevel: "",
      }))
      
      // Check if B level exists
      if (bLevelOptions.length === 0) {
        // No B level orgs, C is the deepest - show it as a team
        const selectedCOrg = allOrganizationsByLevel.C.find(org => org.id === selectedId)
        if (selectedCOrg) {
          const teamsFromOrgs = [{
            id: selectedCOrg.id,
            name: selectedCOrg.name,
            organizationId: selectedCOrg.id,
            organizationName: selectedCOrg.name,
            organizationType: 'C',
            organizationLevel: 'C Level',
            memberCount: getAgentCountForOrganization(selectedCOrg.id),
            maxMembers: 100,
            managerName: (selectedCOrg as any).managerName || 'Manager'
          }]
          setVisibleTeams(teamsFromOrgs)
        }
      } else {
        // Check if any B level orgs have A level children
        const hasALevelChildren = bLevelOptions.some(bOrg => 
          allOrganizationsByLevel.A.some(aOrg => aOrg.parentId === bOrg.id)
        )
        
        if (!hasALevelChildren) {
          // B level exists but none have A children - show B as teams directly
          const teamsFromOrgs = bLevelOptions.map(org => ({
            id: org.id,
            name: org.name,
            organizationId: org.id,
            organizationName: org.name,
            organizationType: 'B',
            organizationLevel: 'B Level',
            memberCount: getAgentCountForOrganization(org.id),
            maxMembers: 100,
            managerName: (org as any).managerName || 'Manager'
          }))
          setVisibleTeams(teamsFromOrgs)
          // Clear B level dropdown since we're showing them as teams
          setCascadingOptions((prev) => ({
            ...prev,
            bLevel: [],
          }))
        } else {
          // Has A level children, don't show teams yet - wait for B selection
          setVisibleTeams([])
        }
      }
    } else if (level === "bLevel") {
      const newSelection = {
        ...currentSelection,
        bLevel: selectedId,
      }
      setCurrentSelection(newSelection)
      
      // Filter A level organizations from already loaded data
      const aLevelOptions = allOrganizationsByLevel.A.filter(org => org.parentId === selectedId)
      
      // Only show teams if there are NO child organizations (deepest level)
      if (aLevelOptions.length === 0) {
        // No A level orgs, B is the deepest - show it as a team
        const selectedBOrg = allOrganizationsByLevel.B.find(org => org.id === selectedId)
        if (selectedBOrg) {
          const teamsFromOrgs = [{
            id: selectedBOrg.id,
            name: selectedBOrg.name,
            organizationId: selectedBOrg.id,
            organizationName: selectedBOrg.name,
            organizationType: 'B',
            organizationLevel: 'B Level',
            memberCount: getAgentCountForOrganization(selectedBOrg.id),
            maxMembers: 100,
            managerName: (selectedBOrg as any).managerName || 'Manager'
          }]
          setVisibleTeams(teamsFromOrgs)
        }
      } else {
        // Has A level orgs, show them as teams (A is always the deepest possible level)
        const teamsFromOrgs = aLevelOptions.map(org => ({
          id: org.id,
          name: org.name,
          organizationId: org.id,
          organizationName: org.name,
          organizationType: 'A',
          organizationLevel: 'A Level',
          memberCount: getAgentCountForOrganization(org.id),
          maxMembers: 100,
          managerName: (org as any).managerName || 'Manager'
        }))
        setVisibleTeams(teamsFromOrgs)
      }
    }
  }

  const updateBatchCascadingOptions = (level: string, selectedId: string) => {
    if (level === "eLevel") {
      // Filter D level organizations from already loaded data
      const dLevelOptions = allOrganizationsByLevel.D.filter(org => org.parentId === selectedId)
      
      setCascadingOptions((prev) => ({
        ...prev,
        dLevel: dLevelOptions,
        cLevel: [],
        bLevel: [],
      }))
      setBatchAssignment({
        eLevel: selectedId,
        dLevel: "",
        cLevel: "",
        bLevel: "",
        teamId: "",
      })
      
      // Check if D level exists
      if (dLevelOptions.length === 0) {
        // No D level orgs, E is the deepest - show it as a team
        const selectedEOrg = allOrganizationsByLevel.E.find(org => org.id === selectedId)
        if (selectedEOrg) {
          const teamsFromOrgs = [{
            id: selectedEOrg.id,
            name: selectedEOrg.name,
            organizationId: selectedEOrg.id,
            organizationName: selectedEOrg.name,
            organizationType: 'E',
            organizationLevel: 'E Level',
            memberCount: getAgentCountForOrganization(selectedEOrg.id),
            maxMembers: 100,
            managerName: (selectedEOrg as any).managerName || 'Manager'
          }]
          setTeams(teamsFromOrgs)
        }
      } else {
        // Check if any D level orgs have C level children
        const hasCLevelChildren = dLevelOptions.some(dOrg => 
          allOrganizationsByLevel.C.some(cOrg => cOrg.parentId === dOrg.id)
        )
        
        if (!hasCLevelChildren) {
          // D level exists but none have C children - show D as teams directly
          const teamsFromOrgs = dLevelOptions.map(org => ({
            id: org.id,
            name: org.name,
            organizationId: org.id,
            organizationName: org.name,
            organizationType: 'D',
            organizationLevel: 'D Level',
            memberCount: getAgentCountForOrganization(org.id),
            maxMembers: 100,
            managerName: (org as any).managerName || 'Manager'
          }))
          setTeams(teamsFromOrgs)
          // Clear D level dropdown since we're showing them as teams
          setCascadingOptions((prev) => ({
            ...prev,
            dLevel: [],
          }))
        } else {
          // Has C level children, don't show teams yet - wait for D selection
          setTeams([])
        }
      }
    } else if (level === "dLevel") {
      // Filter C level organizations from already loaded data
      const cLevelOptions = allOrganizationsByLevel.C.filter(org => org.parentId === selectedId)
      
      setCascadingOptions((prev) => ({
        ...prev,
        cLevel: cLevelOptions,
        bLevel: [],
      }))
      setBatchAssignment((prev) => ({
        ...prev,
        dLevel: selectedId,
        cLevel: "",
        bLevel: "",
        teamId: "",
      }))
      
      // Check if C level exists
      if (cLevelOptions.length === 0) {
        // No C level orgs, D is the deepest - show it as a team
        const selectedDOrg = allOrganizationsByLevel.D.find(org => org.id === selectedId)
        if (selectedDOrg) {
          const teamsFromOrgs = [{
            id: selectedDOrg.id,
            name: selectedDOrg.name,
            organizationId: selectedDOrg.id,
            organizationName: selectedDOrg.name,
            organizationType: 'D',
            organizationLevel: 'D Level',
            memberCount: getAgentCountForOrganization(selectedDOrg.id),
            maxMembers: 100,
            managerName: (selectedDOrg as any).managerName || 'Manager'
          }]
          setTeams(teamsFromOrgs)
        }
      } else {
        // Check if any C level orgs have B level children
        const hasBLevelChildren = cLevelOptions.some(cOrg => 
          allOrganizationsByLevel.B.some(bOrg => bOrg.parentId === cOrg.id)
        )
        
        if (!hasBLevelChildren) {
          // C level exists but none have B children - show C as teams directly
          const teamsFromOrgs = cLevelOptions.map(org => ({
            id: org.id,
            name: org.name,
            organizationId: org.id,
            organizationName: org.name,
            organizationType: 'C',
            organizationLevel: 'C Level',
            memberCount: getAgentCountForOrganization(org.id),
            maxMembers: 100,
            managerName: (org as any).managerName || 'Manager'
          }))
          setTeams(teamsFromOrgs)
          // Clear C level dropdown since we're showing them as teams
          setCascadingOptions((prev) => ({
            ...prev,
            cLevel: [],
          }))
        } else {
          // Has B level children, don't show teams yet - wait for C selection
          setTeams([])
        }
      }
    } else if (level === "cLevel") {
      // Filter B level organizations from already loaded data
      const bLevelOptions = allOrganizationsByLevel.B.filter(org => org.parentId === selectedId)
      
      setCascadingOptions((prev) => ({
        ...prev,
        bLevel: bLevelOptions,
      }))
      setBatchAssignment((prev) => ({
        ...prev,
        cLevel: selectedId,
        bLevel: "",
        teamId: "",
      }))
      
      // Check if B level exists
      if (bLevelOptions.length === 0) {
        // No B level orgs, C is the deepest - show it as a team
        const selectedCOrg = allOrganizationsByLevel.C.find(org => org.id === selectedId)
        if (selectedCOrg) {
          const teamsFromOrgs = [{
            id: selectedCOrg.id,
            name: selectedCOrg.name,
            organizationId: selectedCOrg.id,
            organizationName: selectedCOrg.name,
            organizationType: 'C',
            organizationLevel: 'C Level',
            memberCount: getAgentCountForOrganization(selectedCOrg.id),
            maxMembers: 100,
            managerName: (selectedCOrg as any).managerName || 'Manager'
          }]
          setTeams(teamsFromOrgs)
        }
      } else {
        // Check if any B level orgs have A level children
        const hasALevelChildren = bLevelOptions.some(bOrg => 
          allOrganizationsByLevel.A.some(aOrg => aOrg.parentId === bOrg.id)
        )
        
        if (!hasALevelChildren) {
          // B level exists but none have A children - show B as teams directly
          const teamsFromOrgs = bLevelOptions.map(org => ({
            id: org.id,
            name: org.name,
            organizationId: org.id,
            organizationName: org.name,
            organizationType: 'B',
            organizationLevel: 'B Level',
            memberCount: getAgentCountForOrganization(org.id),
            maxMembers: 100,
            managerName: (org as any).managerName || 'Manager'
          }))
          setTeams(teamsFromOrgs)
          // Clear B level dropdown since we're showing them as teams
          setCascadingOptions((prev) => ({
            ...prev,
            bLevel: [],
          }))
        } else {
          // Has A level children, don't show teams yet - wait for B selection
          setTeams([])
        }
      }
    } else if (level === "bLevel") {
      const newBatchAssignment = {
        ...batchAssignment,
        bLevel: selectedId,
        teamId: "",
      }
      setBatchAssignment(newBatchAssignment)
      
      // Filter A level organizations from already loaded data
      const aLevelOptions = allOrganizationsByLevel.A.filter(org => org.parentId === selectedId)
      
      // Only show teams if there are NO child organizations (deepest level)
      if (aLevelOptions.length === 0) {
        // No A level orgs, B is the deepest - show it as a team
        const selectedBOrg = allOrganizationsByLevel.B.find(org => org.id === selectedId)
        if (selectedBOrg) {
          const teamsFromOrgs = [{
            id: selectedBOrg.id,
            name: selectedBOrg.name,
            organizationId: selectedBOrg.id,
            organizationName: selectedBOrg.name,
            organizationType: 'B',
            organizationLevel: 'B Level',
            memberCount: getAgentCountForOrganization(selectedBOrg.id),
            maxMembers: 100,
            managerName: (selectedBOrg as any).managerName || 'Manager'
          }]
          setTeams(teamsFromOrgs)
        }
      } else {
        // Has A level orgs, show them as teams (A is always the deepest possible level)
        const teamsFromOrgs = aLevelOptions.map(org => ({
          id: org.id,
          name: org.name,
          organizationId: org.id,
          organizationName: org.name,
          organizationType: 'A',
          organizationLevel: 'A Level',
          memberCount: getAgentCountForOrganization(org.id),
          maxMembers: 100,
          managerName: (org as any).managerName || 'Manager'
        }))
        setTeams(teamsFromOrgs)
      }
    }
  }

  useEffect(() => {
    const loadFilteredAgents = async () => {
      try {
        const agentsData = await HRService.getAgentsForAssignment({
          search: filters.search || undefined,
          status: filters.status || undefined,
        })
        setFilteredAgents(agentsData.agents)
      } catch (error) {
        console.error("Error filtering agents:", error)
        onError("Failed to filter agents. Please try again.")
      }
    }

    loadFilteredAgents()
  }, [filters, onError])

  const handleDragStart = (agent: Agent) => {
    setDraggedAgent(agent)
  }

  const handleDragOver = (e: React.DragEvent, teamId?: string) => {
    e.preventDefault()
    if (teamId) {
      setDragOverTeam(teamId)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTeam(null)
  }

  const handleDrop = async (e: React.DragEvent, team: Team) => {
    e.preventDefault()
    setDragOverTeam(null)
    
    if (draggedAgent) {
      // Check if agent is already assigned to this organization
      // Note: team.id actually holds the organizationId in our current system
      if (draggedAgent.organizationId === team.id) {
        onError(`${draggedAgent.firstName} ${draggedAgent.lastName} is already assigned to ${team.name}`)
        setDraggedAgent(null)
        return
      }

      // Validate team data before assignment
      if (!team.organizationId) {
        console.error("❌ Team missing organizationId:", team)
        onError("Team data is incomplete. Please refresh and try again.")
        setDraggedAgent(null)
        return
      }

   
      await assignAgentToTeam(draggedAgent.id, team.id)
      setDraggedAgent(null)
    } else {
      console.error("❌ No dragged agent found in handleDrop")
    }
  }

  const loadOrganizationsByType = async (type: string, parentId?: string) => {
    try {
      setLoadingOrganizations(true)
      
      // Map frontend types to database types
      let dbType: string
      switch (type) {
        case 'E':
          dbType = 'EM'
          break
        case 'D':
          dbType = 'DM'
          break
        case 'C':
          dbType = 'CM'
          break
        case 'B':
          dbType = 'BM'
          break
        case 'A':
          dbType = 'AM'
          break
        default:
          dbType = type
      }
      
      // Use the correct service method to get organizations by type
      const organizationsData = await OrganizationService.getOrganizationsByLevel(dbType as any)
      
      // Map database organization types to frontend types
      const mappedOrgs = organizationsData.map((org) => ({
        ...org,
        type: type as 'E' | 'D' | 'C' | 'B' | 'A'
      }))
      
      return mappedOrgs
    } catch (error) {
      console.error(`❌ Error loading organizations for type ${type}:`, error)
      return []
    } finally {
      setLoadingOrganizations(false)
    }
  }

  const refreshTeamsData = () => {
    // Just update the member counts for currently visible teams
    setVisibleTeams(prevTeams => 
      prevTeams.map(team => ({
        ...team,
        memberCount: getAgentCountForOrganization(team.id)
      }))
    )
    setTeams(prevTeams => 
      prevTeams.map(team => ({
        ...team,
        memberCount: getAgentCountForOrganization(team.id)
      }))
    )
  }

  const assignAgentToTeam = async (agentId: string, organizationId: string) => {
   
    const agent = agents.find((a) => a.id === agentId)
    // Find organization in teams/visibleTeams (which are actually organizations displayed as teams)
    const targetOrg = teams.find((t) => t.id === organizationId) || visibleTeams.find((t) => t.id === organizationId)
    
    if (!agent || !targetOrg) {
      if (!agent) console.error("  - Agent not found")
      if (!targetOrg) console.error("  - Organization not found")
      return
    }

    // Check if agent is already assigned to this organization (client-side check)
    if (agent.organizationId === organizationId) {
      onError(`${agent.firstName} ${agent.lastName} is already assigned to ${targetOrg.name}`)
      return
    }

    // Optimistic update - update UI immediately
    setAssigningAgent(agentId)
    
    // Set a timeout to clear loading state in case of unexpected errors
    const timeoutId = setTimeout(() => {
      setAssigningAgent(null)
    }, 10000) // 10 second timeout
    
    // Store the previous organization ID for rollback
    const previousOrgId = agent.organizationId
    
    // Update agent state optimistically
    setAgents((prev) =>
      prev.map((a) =>
        a.id === agentId
          ? {
              ...a,
              organizationId,
              teamId: organizationId,
              organizationName: targetOrg.name,
              teamName: targetOrg.name,
              status: "ACTIVE" as const,
            }
          : a,
      ),
    )

    // Update organization member counts optimistically
    const updateOrgCounts = (orgList: Team[]) =>
      orgList.map((t) => {
        if (t.id === organizationId) {
          // Increase count for new organization
          return {
            ...t,
            memberCount: Math.min(t.memberCount + 1, t.maxMembers),
          }
        } else if (previousOrgId && t.id === previousOrgId) {
          // Decrease count for old organization (if agent was previously assigned)
          return {
            ...t,
            memberCount: Math.max(t.memberCount - 1, 0),
          }
        }
        return t
      })

    setTeams(updateOrgCounts)
    setVisibleTeams(updateOrgCounts)

    // Show animation for updated organizations
    const orgsToAnimate = [organizationId]
    if (previousOrgId) {
      orgsToAnimate.push(previousOrgId)
    }
    
    setRecentlyUpdatedTeams(prev => new Set(Array.from(prev).concat(orgsToAnimate)))
    setTimeout(() => {
      setRecentlyUpdatedTeams(prev => {
        const newSet = new Set(prev)
        orgsToAnimate.forEach(orgId => newSet.delete(orgId))
        return newSet
      })
    }, 2000) // Remove animation after 2 seconds

    try {
      await HRService.assignAgentToTeam({
        agentId: agentId,
        organizationId: organizationId,
      })
      refreshTeamsData()

    } catch (error: any) {
      console.error("Error assigning agent to organization:", error)
      
      // Revert optimistic updates on error
      setAgents((prev) =>
        prev.map((a) =>
          a.id === agentId
            ? {
                ...a,
                organizationId: agent.organizationId,
                teamId: agent.teamId,
                organizationName: agent.organizationName,
                teamName: agent.teamName,
                status: agent.status,
              }
            : a,
        ),
      )

      const revertOrgCounts = (orgList: Team[]) =>
        orgList.map((t) => {
          if (t.id === organizationId) {
            // Revert new organization count
            return {
              ...t,
              memberCount: Math.max(0, t.memberCount - 1),
            }
          } else if (previousOrgId && t.id === previousOrgId) {
            // Restore old organization count
            return {
              ...t,
              memberCount: t.memberCount + 1,
            }
          }
          return t
        })

      setTeams(revertOrgCounts)
      setVisibleTeams(revertOrgCounts)

      // Show specific error message - handle different error response formats
      let errorMessage = "Failed to assign agent to organization"
      
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }
      
      // Make error messages more user-friendly
      if (errorMessage.includes("already assigned")) {
        errorMessage = `${agent.firstName} ${agent.lastName} is already assigned to this organization`
      } else if (errorMessage.includes("not found")) {
        errorMessage = "Organization not found. Please refresh and try again."
      } else if (errorMessage.includes("inactive")) {
        errorMessage = "Cannot assign to inactive organization"
      } else if (errorMessage.includes("Foreign key constraint")) {
        errorMessage = "Database error: Unable to complete assignment. Please contact support."
      }
      
      onError(errorMessage)
    } finally {
      // Clear timeout and loading state
      clearTimeout(timeoutId)
      setAssigningAgent(null)
    }
  }

  const handleSelectAgent = (agentId: string) => {
    const newSelected = new Set(selectedAgents)
    if (newSelected.has(agentId)) {
      newSelected.delete(agentId)
    } else {
      newSelected.add(agentId)
    }
    setSelectedAgents(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedAgents.size === filteredAgents.length) {
      setSelectedAgents(new Set())
    } else {
      setSelectedAgents(new Set(filteredAgents.map((agent) => agent.id)))
    }
  }

  const handleBatchAssign = async () => {
    if (selectedAgents.size === 0 || !batchAssignment.eLevel || !batchAssignment.teamId) {
      onError("Please select licensed agents and choose organization")
      return
    }

    const selectedOrg = teams.find((t) => t.id === batchAssignment.teamId)
    if (!selectedOrg) return

    try {
    
      await HRService.batchAssignAgents({
        agentIds: Array.from(selectedAgents),
        organizationId: batchAssignment.teamId,
      })
      refreshTeamsData()

      // Update local state to reflect the changes
      const agentsToUpdate = Array.from(selectedAgents)
      const previousOrgIds = new Set<string>()
      
      // Collect previous organization IDs for count adjustments
      agentsToUpdate.forEach((agentId) => {
        const agent = agents.find(a => a.id === agentId)
        if (agent?.organizationId) {
          previousOrgIds.add(agent.organizationId)
        }
      })

      // Update agents
      agentsToUpdate.forEach((agentId) => {
        setAgents((prev) =>
          prev.map((agent) =>
            agent.id === agentId
              ? {
                  ...agent,
                  organizationId: batchAssignment.teamId,
                  teamId: batchAssignment.teamId,
                  organizationName: selectedOrg.name,
                  teamName: selectedOrg.name,
                  status: "ACTIVE" as const,
                }
              : agent,
          ),
        )
      })

      // Update organization counts
      const updateBatchOrgCounts = (orgList: Team[]) =>
        orgList.map((t) => {
          if (t.id === batchAssignment.teamId) {
            // Increase count for new organization
            return {
              ...t,
              memberCount: Math.min(t.memberCount + agentsToUpdate.length, t.maxMembers),
            }
          } else if (previousOrgIds.has(t.id)) {
            // Decrease count for old organizations
            const agentsFromThisOrg = agentsToUpdate.filter(agentId => {
              const agent = agents.find(a => a.id === agentId)
              return agent?.organizationId === t.id
            }).length
            return {
              ...t,
              memberCount: Math.max(t.memberCount - agentsFromThisOrg, 0),
            }
          }
          return t
        })

      setTeams(updateBatchOrgCounts)
      setVisibleTeams(updateBatchOrgCounts)

      // Show animation for updated organizations
      const orgsToAnimate = [batchAssignment.teamId, ...Array.from(previousOrgIds)]
      setRecentlyUpdatedTeams(prev => new Set(Array.from(prev).concat(orgsToAnimate)))
      setTimeout(() => {
        setRecentlyUpdatedTeams(prev => {
          const newSet = new Set(prev)
          orgsToAnimate.forEach(orgId => newSet.delete(orgId))
          return newSet
        })
      }, 2000)

    setSelectedAgents(new Set())
    setShowBatchModal(false)
    setBatchAssignment({
      eLevel: "",
      dLevel: "",
      cLevel: "",
      bLevel: "",
      teamId: "",
    })
    } catch (error: any) {
      console.error("Error batch assigning agents:", error)
      
      // Show specific error message - handle different error response formats
      let errorMessage = "Failed to batch assign agents. Please try again."
      
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }
      
      // Make error messages more user-friendly
      if (errorMessage.includes("already assigned")) {
        errorMessage = "One or more agents are already assigned to this organization"
      } else if (errorMessage.includes("not found")) {
        errorMessage = "Organization not found. Please refresh and try again."
      } else if (errorMessage.includes("inactive")) {
        errorMessage = "Cannot assign to inactive organization or agents"
      } else if (errorMessage.includes("Foreign key constraint")) {
        errorMessage = "Database error: Unable to complete batch assignment. Please contact support."
      }
      
      onError(errorMessage)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800"
      case "PENDING":
        return "bg-yellow-100 text-yellow-800"
      case "DEACTIVATED":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  // if (loading) {
  //   return (
  //     <div className="space-y-6">
  //       <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
  //         <div>
  //           <h2 className="text-2xl font-bold text-accent-900">Agent Assignment</h2>
  //           <p className="text-sm text-accent-600 mt-1">Assign new agents to teams and organizations</p>
  //         </div>
  //       </div>
  //       <div className="card">
  //         <div className="flex items-center justify-center py-12">
  //           <div className="text-center">
  //             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
  //             <p className="text-accent-600">Loading agents and organizations...</p>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   )
  // }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">Licensed Agent Assignment</h2>
          <p className="text-sm text-accent-600 mt-1">Assign new licensed agents to teams and organizations</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowBatchModal(true)}
            disabled={selectedAgents.size === 0}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4" />
            <span>Batch Assign ({selectedAgents.size})</span>
          </button>
        </div>
      </div>

      <div className="card">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search licensed agents..."
                className="input-field pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div>
              <select
                className="input-field"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="ACTIVE">Active</option>
                <option value="DEACTIVATED">Deactivated</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-accent-700">Select Organization</h4>
            {loadingOrganizations ? (
              <OrganizationHierarchyShimmer />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-accent-600 mb-1">E Level</label>
                  <select
                    className="input-field text-sm"
                    value={currentSelection.eLevel}
                    onChange={(e) => updateCascadingOptions("eLevel", e.target.value)}
                  >
                    <option value="">Select E Level</option>
                    {cascadingOptions.eLevel.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name} - {org.managerName}
                      </option>
                    ))}
                  </select>
                </div>

                {currentSelection.eLevel && cascadingOptions.dLevel.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-accent-600 mb-1">D Level</label>
                    <select
                      className="input-field text-sm"
                      value={currentSelection.dLevel}
                      onChange={(e) => updateCascadingOptions("dLevel", e.target.value)}
                    >
                      <option value="">Select D Level</option>
                      {cascadingOptions.dLevel.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} - {org.managerName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {currentSelection.dLevel && cascadingOptions.cLevel.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-accent-600 mb-1">C Level</label>
                    <select
                      className="input-field text-sm"
                      value={currentSelection.cLevel}
                      onChange={(e) => updateCascadingOptions("cLevel", e.target.value)}
                    >
                      <option value="">Select C Level</option>
                      {cascadingOptions.cLevel.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} - {org.managerName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {currentSelection.cLevel && cascadingOptions.bLevel.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-accent-600 mb-1">B Level</label>
                    <select
                      className="input-field text-sm"
                      value={currentSelection.bLevel}
                      onChange={(e) => updateCascadingOptions("bLevel", e.target.value)}
                    >
                      <option value="">Select B Level</option>
                      {cascadingOptions.bLevel.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} - {org.managerName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-accent-900">Licensed Agents ({filteredAgents.length})</h3>
            <button onClick={handleSelectAll} className="text-sm text-primary-600 hover:text-primary-800">
              {selectedAgents.size === filteredAgents.length ? "Deselect All" : "Select All"}
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loading ? (
              <AgentAssignmentShimmer rows={5} />
            ) : (
              filteredAgents.map((agent) => (
              <div
                key={agent.id}
                draggable={!assigningAgent}
                onDragStart={() => handleDragStart(agent)}
                className={`p-3 border rounded-lg transition-all duration-200 ${
                  assigningAgent === agent.id
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-move hover:bg-accent-50 hover:shadow-sm"
                } ${
                  selectedAgents.has(agent.id) ? "border-primary-300 bg-primary-50" : "border-accent-200"
                } ${
                  draggedAgent?.id === agent.id ? "opacity-50 scale-95" : ""
                }`}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedAgents.has(agent.id)}
                    onChange={() => handleSelectAgent(agent.id)}
                    disabled={assigningAgent === agent.id}
                    className="rounded border-accent-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-accent-900 truncate">
                        {agent.firstName} {agent.lastName}
                        {assigningAgent === agent.id && (
                          <span className="ml-2 text-xs text-blue-600">Assigning...</span>
                        )}
                      </p>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(agent.status)}`}>
                        {agent.status}
                      </span>
                    </div>
                    <p className="text-xs text-accent-500 truncate">{agent.email}</p>
                    {agent.organizationName && agent.teamName && (
                      <p className="text-xs text-accent-600 truncate">
                        {agent.organizationName} → {agent.teamName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-accent-900 mb-4">
            Teams {currentSelection.eLevel ? `(${visibleTeams.length})` : ""}
          </h3>
          {!currentSelection.eLevel ? (
            <div className="text-center py-8 text-accent-500">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Select E level organization to view teams</p>
            </div>
          ) : loadingTeams ? (
            <div className="max-h-96 overflow-y-auto">
              <TeamShimmer rows={3} />
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {visibleTeams.map((team) => (
                <div
                  key={team.id}
                  onDragOver={(e) => handleDragOver(e, team.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, team)}
                  className={`p-4 border-2 border-dashed rounded-lg transition-all duration-200 ${
                    dragOverTeam === team.id
                      ? "border-primary-500 bg-primary-100 scale-105 shadow-lg"
                      : "border-accent-300 hover:border-primary-400 hover:bg-primary-50"
                  } ${
                    draggedAgent && draggedAgent.organizationId === team.id
                      ? "border-red-300 bg-red-50"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-accent-900">{team.name}</h4>
                      <p className="text-sm text-accent-600">{team.organizationName}</p>
                      {team.organizationLevel && (
                        <p className="text-xs text-blue-600 font-medium">{team.organizationLevel}</p>
                      )}
                      <p className="text-xs text-accent-500">Manager: {team.managerName}</p>
                      <div className="flex items-center gap-2">
                      <p className="text-xs text-accent-500">
                          <span className={`font-semibold transition-all duration-500 ${
                            recentlyUpdatedTeams.has(team.id)
                              ? "text-green-600 scale-110 animate-pulse"
                              : team.memberCount > 0 
                                ? "text-green-600" 
                                : "text-accent-500"
                          }`}>
                            {team.memberCount}
                          </span>
                          /{team.maxMembers} members
                        </p>
                        {dragOverTeam === team.id && draggedAgent && (
                          <span className="text-xs text-blue-600 font-medium animate-pulse">
                            Drop {draggedAgent.firstName} {draggedAgent.lastName} here
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                        dragOverTeam === team.id
                          ? "bg-primary-200"
                          : "bg-primary-100"
                      }`}>
                        <Users className="w-6 h-6 text-primary-600" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 w-full bg-accent-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        recentlyUpdatedTeams.has(team.id)
                          ? "bg-green-500 animate-pulse"
                          : "bg-primary-600"
                      }`}
                      style={{ width: `${Math.min((team.memberCount / team.maxMembers) * 100, 100)}%` }}
                    ></div>
                  </div>
                  {team.memberCount >= team.maxMembers && (
                    <p className="text-xs text-red-600 mt-1 font-medium">Team is full!</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-accent-900">Batch Assign {selectedAgents.size} Licensed Agents</h3>
              <button onClick={() => setShowBatchModal(false)} className="text-accent-400 hover:text-accent-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-medium text-accent-700 mb-3">Select Organization Hierarchy *</h4>
                {loadingOrganizations ? (
                  <OrganizationHierarchyShimmer />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-accent-600 mb-1">E Level</label>
                      <select
                        className="input-field text-sm"
                        value={batchAssignment.eLevel}
                        onChange={(e) => updateBatchCascadingOptions("eLevel", e.target.value)}
                      >
                        <option value="">Select E Level</option>
                        {cascadingOptions.eLevel.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name} - {org.managerName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {batchAssignment.eLevel && cascadingOptions.dLevel.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-accent-600 mb-1">D Level</label>
                        <select
                          className="input-field text-sm"
                          value={batchAssignment.dLevel}
                          onChange={(e) => updateBatchCascadingOptions("dLevel", e.target.value)}
                        >
                          <option value="">Select D Level</option>
                          {cascadingOptions.dLevel.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name} - {org.managerName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {batchAssignment.dLevel && cascadingOptions.cLevel.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-accent-600 mb-1">C Level</label>
                        <select
                          className="input-field text-sm"
                          value={batchAssignment.cLevel}
                          onChange={(e) => updateBatchCascadingOptions("cLevel", e.target.value)}
                        >
                          <option value="">Select C Level</option>
                          {cascadingOptions.cLevel.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name} - {org.managerName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {batchAssignment.cLevel && cascadingOptions.bLevel.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-accent-600 mb-1">B Level</label>
                        <select
                          className="input-field text-sm"
                          value={batchAssignment.bLevel}
                          onChange={(e) => updateBatchCascadingOptions("bLevel", e.target.value)}
                        >
                          <option value="">Select B Level</option>
                          {cascadingOptions.bLevel.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name} - {org.managerName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-accent-700 mb-1">Team *</label>
                {loadingTeams ? (
                  <div className="h-10 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-full bg-[length:200%_100%] animate-shimmer"></div>
                ) : (
                  <select
                    className="input-field"
                    value={batchAssignment.teamId}
                    onChange={(e) => setBatchAssignment({ ...batchAssignment, teamId: e.target.value })}
                    disabled={!batchAssignment.eLevel}
                  >
                    <option value="">Select Team</option>
                    {batchAssignment.eLevel &&
                      teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name} - {team.managerName} ({team.memberCount}/{team.maxMembers}) {team.organizationLevel && `[${team.organizationLevel}]`}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button onClick={() => setShowBatchModal(false)} className="btn-outline">
                Cancel
              </button>
              <button
                onClick={handleBatchAssign}
                className="btn-primary flex items-center space-x-2"
                disabled={!batchAssignment.eLevel || !batchAssignment.teamId}
              >
                <Check className="w-4 h-4" />
                <span>Assign Licensed Agents</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
